import {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {TablerMicrophone, TablerSquare} from '../../icons';
import useDictationSession, {
  DICTATION_PHASE,
} from '../../hooks/useDictationSession';
import usePlannerStyles from '../planner/usePlannerStyles';
import AudioWaveform from '../common/AudioWaveform';

// ---------------------------------------------------------------------------
// TeDictationButton
//
// Inline "Voice edit" for a single telephone-encounter text field (the Message
// or an Action Taken note). Like the scribe `DictationFab`, the clinician
// speaks and the transcript is routed through an LLM that arbitrates between
// applying a command ("remove the line about the telephone encounter", "make
// it more concise") and folding in new content — the difference is the TE is a
// single free-text field, so there's no section routing. The caller's
// `onInsert` receives the final transcript and resolves once the edit has been
// applied to the field, so the button can hold an "Applying…" state while the
// backend thinks. (When a caller wires `onInsert` to a plain append instead,
// this still works as straight dictation.)
//
// Built on the low-level `useDictationSession` hook (mic + Soniox), not the
// FAB, so it composes inline with the Message composer and the Action Taken
// thread. The active UI mirrors the note-preview "Voice edit" affordance —
// same live waveform, listening state, rolling transcript, and finish handling
// — so it feels just as responsive and trustworthy as voice edit in the note
// preview.
// ---------------------------------------------------------------------------

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const ss = (total % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

// Grayscale waveform theme, matching the DictationFab pill so the two
// dictation surfaces read as the same feature.
const DICTATION_GRADIENT_STOPS = [
  [0, 'rgba(99, 99, 99, 0.95)'],
  [0.5, 'rgba(99, 99, 99, 0.75)'],
  [1, 'rgba(193, 193, 193, 0.9)'],
];
const DICTATION_GLOW_PREFIX = 'rgba(99, 99, 99,';

// Same Opal blue the note-preview Voice edit button uses, so the affordance
// reads as the identical feature across surfaces.
const VOICE_EDIT_BLUE = '#117DB1';
const VOICE_EDIT_BLUE_HOVER = '#0E6B98';
const VOICE_EDIT_BLUE_ACTIVE = '#0C5C82';

const TeDictationButton = ({
  onInsert,
  isDisabled = false,
  label = 'Voice edit',
  applyingLabel = 'Applying voice edit…',
  maxDurationMs,
}: {
  // Receives the final transcript when the clinician stops. The caller applies
  // it to the field (typically a backend voice edit) and resolves the returned
  // promise once done, so the button can show an "Applying…" state meanwhile.
  onInsert: (text: string) => void | Promise<void>;
  isDisabled?: boolean;
  label?: string;
  // Status text while the caller's onInsert is in flight — dictation-compose
  // surfaces pass "Formatting dictation…" so the state reads truthfully.
  applyingLabel?: string;
  // Overrides the hook's 2-minute safety cap for surfaces where a longer take
  // is legitimate (dictating a full message with lab results).
  maxDurationMs?: number;
}) => {
  const {borderColor, subtleText, textColor, panelBg, accentColor} =
    usePlannerStyles();
  // Latest insert handler, for the hook's auto-stop callback (armed when
  // recording starts, fired up to 2 minutes later).
  const insertTranscriptRef = useRef<(text: string) => Promise<void>>();
  const {
    phase,
    isRecording,
    fullTranscript,
    finalTranscript,
    interimTranscript,
    elapsedMs,
    error,
    start,
    stop,
    reset,
    waveformSource,
  } = useDictationSession({
    // When the hook's safety cap stops the recording, insert what was
    // captured — same as pressing Stop & Apply. Without this the transcript
    // would be silently discarded.
    onAutoStop: (finalText: string) => {
      void insertTranscriptRef.current?.(finalText);
    },
    maxDurationMs,
  });

  // Footer strip behind Cancel / Stop & Apply — the theme-aware analog of the
  // FAB's `gray.50` action bar.
  const footerBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  // True while the caller's onInsert (the backend voice edit) is in flight,
  // after Soniox has finalized but before the field updates. Kept local rather
  // than reusing the hook's APPLYING phase so the button owns its own
  // post-transcript lifecycle.
  const [isApplying, setIsApplying] = useState(false);

  const isConnecting = phase === DICTATION_PHASE.CONNECTING;
  const isStopping = phase === DICTATION_PHASE.STOPPING;
  const isError = phase === DICTATION_PHASE.ERROR;
  const isActive = isConnecting || isRecording || isStopping || isApplying;

  const liveStatusRef = useRef<HTMLDivElement | null>(null);

  const insertTranscript = useCallback(
    async (finalText: string) => {
      const trimmed = (finalText || '').trim();
      if (!trimmed) {
        reset();
        return;
      }
      setIsApplying(true);
      try {
        await onInsert(trimmed);
      } finally {
        setIsApplying(false);
        reset();
      }
    },
    [onInsert, reset],
  );

  useEffect(() => {
    insertTranscriptRef.current = insertTranscript;
  }, [insertTranscript]);

  const handleStop = useCallback(async () => {
    const finalText = await stop();
    await insertTranscript(finalText || '');
  }, [stop, insertTranscript]);

  const handleCancel = useCallback(async () => {
    await stop();
    reset();
  }, [stop, reset]);

  // Esc stops and inserts while recording, mirroring the Voice edit shortcut.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (phase === DICTATION_PHASE.RECORDING) {
        e.preventDefault();
        void handleStop();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, handleStop]);

  // aria-live announcements for screen readers, matching the FAB.
  useEffect(() => {
    const el = liveStatusRef.current;
    if (!el) return;
    const map: Record<string, string> = {
      [DICTATION_PHASE.CONNECTING]: 'Connecting microphone',
      [DICTATION_PHASE.RECORDING]: 'Listening — dictate your note',
      [DICTATION_PHASE.STOPPING]: 'Finishing dictation',
      [DICTATION_PHASE.ERROR]: 'Dictation error',
    };
    el.textContent = isApplying
      ? applyingLabel.replace(/…$/, '')
      : map[phase] || '';
  }, [phase, isApplying, applyingLabel]);

  const liveRegion = (
    <Box
      ref={liveStatusRef}
      aria-live="polite"
      aria-atomic="true"
      position="absolute"
      width="1px"
      height="1px"
      overflow="hidden"
      sx={{clip: 'rect(0 0 0 0)'}}
    />
  );

  // Applying: spinner + status + a quote of what was heard — the FAB's
  // "Applying to your note…" treatment.
  if (isApplying) {
    return (
      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="24px"
        overflow="hidden"
        w={{base: '100%', sm: '380px'}}
        maxW="100%"
        px={4}
        py={3}
      >
        {liveRegion}
        <HStack spacing={3} minW={0}>
          <Spinner size="sm" color={accentColor} flexShrink={0} />
          <VStack spacing={0} align="flex-start" flex={1} minW={0}>
            <Text
              fontSize="sm"
              fontWeight="normal"
              color={textColor}
              noOfLines={1}
            >
              {applyingLabel}
            </Text>
            {finalTranscript ? (
              <Text fontSize="xs" color={subtleText} noOfLines={1} w="100%">
                &quot;
                {finalTranscript.slice(0, 60)}
                {finalTranscript.length > 60 ? '…' : ''}
                &quot;
              </Text>
            ) : null}
          </VStack>
        </HStack>
      </Box>
    );
  }

  // Connecting: compact pill (spinner + status + Cancel), like the FAB's
  // connecting state.
  if (isConnecting) {
    return (
      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="24px"
        overflow="hidden"
        w={{base: '100%', sm: '380px'}}
        maxW="100%"
        px={4}
        py={3}
      >
        {liveRegion}
        <HStack spacing={3} minW={0}>
          <Spinner size="sm" color={accentColor} flexShrink={0} />
          <Text
            fontSize="sm"
            fontWeight="normal"
            color={textColor}
            noOfLines={1}
            flex={1}
            minW={0}
          >
            Connecting microphone…
          </Text>
          <Button
            size="xs"
            variant="ghost"
            onClick={handleCancel}
            flexShrink={0}
          >
            Cancel
          </Button>
        </HStack>
      </Box>
    );
  }

  // Recording / stopping: the FAB's recording card — status header, waveform,
  // rolling transcript, then a footer action bar with a ghost Cancel and a
  // solid gray "Stop & Apply" pill.
  if (isActive) {
    return (
      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="24px"
        overflow="hidden"
        w={{base: '100%', sm: '380px'}}
        maxW="100%"
      >
        {liveRegion}
        <Flex px={4} pt={3} pb={2} align="center" justify="space-between">
          <HStack spacing={2} minW={0}>
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg="gray.500"
              flexShrink={0}
              boxShadow="0 0 0 0 rgba(104, 104, 104, 0.4)"
              sx={{
                animation: isRecording ? 'te-dict-pulse 1.4s infinite' : 'none',
                '@keyframes te-dict-pulse': {
                  '0%': {boxShadow: '0 0 0 0 rgba(104, 104, 104, 0.6)'},
                  '70%': {boxShadow: '0 0 0 10px rgba(104, 104, 104, 0)'},
                  '100%': {boxShadow: '0 0 0 0 rgba(104, 104, 104, 0)'},
                },
              }}
            />
            <Text
              fontSize="sm"
              fontWeight="normal"
              color={textColor}
              letterSpacing="0.01em"
            >
              {isStopping ? 'Finishing…' : 'Listening'}
            </Text>
            <Text
              fontSize="xs"
              color={subtleText}
              sx={{fontVariantNumeric: 'tabular-nums'}}
            >
              {formatElapsed(elapsedMs)}
            </Text>
          </HStack>
        </Flex>

        <Box px={3}>
          <AudioWaveform
            sessionManagerRef={waveformSource}
            isRecording={isRecording}
            isPaused={false}
            bars={32}
            width="100%"
            maxWidth="100%"
            height="56px"
            gradientStops={DICTATION_GRADIENT_STOPS}
            glowColorPrefix={DICTATION_GLOW_PREFIX}
            mb={2}
          />
        </Box>

        <Box px={4} pb={2} minH="36px">
          <Text
            fontSize="sm"
            color={fullTranscript ? textColor : subtleText}
            fontStyle={
              fullTranscript && !interimTranscript ? 'normal' : 'italic'
            }
            noOfLines={2}
            lineHeight="1.45"
            aria-live="off"
          >
            {fullTranscript || 'Speak a change or the text you want inserted…'}
          </Text>
        </Box>

        <Flex
          borderTopWidth="1px"
          borderColor={borderColor}
          bg={footerBg}
          px={3}
          py={2}
          gap={2}
          align="center"
          justify="space-between"
        >
          <Button
            size="sm"
            variant="ghost"
            color={subtleText}
            onClick={handleCancel}
            isDisabled={isStopping}
            aria-label="Cancel dictation without applying"
          >
            Cancel
          </Button>
          <Button
            onClick={handleStop}
            isLoading={isStopping}
            loadingText="Finishing"
            size="md"
            minH="44px"
            px={5}
            borderRadius="full"
            bg="gray.500"
            color="white"
            fontWeight="normal"
            leftIcon={<TablerSquare size={16} aria-hidden="true" />}
            _hover={{bg: 'gray.600'}}
            _active={{bg: 'gray.700'}}
            _focusVisible={{
              boxShadow: '0 0 0 3px rgba(104, 104, 104, 0.45)',
            }}
            aria-label="Stop and apply dictation"
          >
            Stop &amp; Apply
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      {liveRegion}
      <Button
        size="md"
        fontSize="sm"
        fontWeight="normal"
        borderRadius="full"
        bg={VOICE_EDIT_BLUE}
        color="white"
        leftIcon={<TablerMicrophone size={16} aria-hidden="true" />}
        onClick={() => start()}
        isDisabled={isDisabled}
        aria-label={label}
        _hover={{bg: VOICE_EDIT_BLUE_HOVER}}
        _active={{bg: VOICE_EDIT_BLUE_ACTIVE}}
        _focusVisible={{boxShadow: `0 0 0 3px ${VOICE_EDIT_BLUE}55`}}
      >
        {label}
      </Button>
      {isError && error ? (
        <Text fontSize="xs" color="red.400" mt={1}>
          {String(error)} — tap {label} to try again.
        </Text>
      ) : null}
    </Box>
  );
};

export default TeDictationButton;
